// Browser-decided exchanges: one command and retry identity, with no server rule execution.
import type { ExchangePlan } from './trade-exchange.js';
import { Refusal } from '../ability/context.js';
import { EXCHANGE_SCHEMA, exchangeValueRefusal } from './exchange-value-contract.js';
export { EXCHANGE_SCHEMA } from './exchange-value-contract.js';
export { favorPrices } from './trade-exchange.js';
export const EXCHANGE_ENDPOINT = '/options/game/actions/inventory_trade_truth_commit_inc.php';
export type ExchangeKind = 'trade' | 'haggle' | 'training';
export interface ExchangeLine { inventory_id: string; qty: number }
export interface ExchangeCommand {
  schema: typeof EXCHANGE_SCHEMA;
  operation_id: string;
  encounter_id: string;
  kind: ExchangeKind;
  actor_id: string;
  counterpart_id: string;
  before: { merchant_favor: number | null };
  plan: ExchangePlan;
  player_offer: ExchangeLine[];
  merchant_offer: ExchangeLine[];
  skill_var?: string;
}
export interface ExchangeAnswer {
  ok: boolean;
  /** HTTP status; 0 = never answered. */
  status: number;
  error?: string;
  /** The body as the server answered it (success fields per kind; `saved.merchant_favor` on a 409). */
  body: Record<string, unknown> | null;
}

const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const newOperationId = (): string => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
  ? crypto.randomUUID()
  : `${Date.now().toString(16)}-0000-4000-8000-${Math.random().toString(16).slice(2, 14).padEnd(12, '0')}`).toUpperCase();

/** Exchanges whose outcome is UNKNOWN (never answered, or a 5xx after the retries), by content: re-accepting the SAME exchange
 * reuses its operation id, so the server answers the recorded outcome instead of trading twice (Codex review INV2 S2 #5). */
const unknownOutcome = new Map<string, ExchangeCommand>();
const contentKey = (c: Omit<ExchangeCommand, 'schema' | 'operation_id' | 'plan'>): string =>
  JSON.stringify([c.kind, c.encounter_id, c.actor_id, c.counterpart_id, c.before.merchant_favor, c.player_offer, c.merchant_offer, c.skill_var ?? '']);

/** Build one exchange command from the panel's decision. Lines keep the panel's order (the legacy move order). */
export function buildExchangeCommand(input: {
  kind: ExchangeKind; encounterId: string; playerId: string; counterpartId: string; merchantFavor: number | null; plan: ExchangePlan;
  playerOffer?: ExchangeLine[]; merchantOffer?: ExchangeLine[]; skillVar?: string; operationId?: string;
}): ExchangeCommand {
  const lines = (rows: ExchangeLine[] | undefined): ExchangeLine[] => (rows ?? []).map(r => ({ inventory_id: upper(r.inventory_id), qty: Math.trunc(Number(r.qty)) }));
  const body = {
    encounter_id: upper(input.encounterId), kind: input.kind,
    actor_id: upper(input.playerId), counterpart_id: upper(input.counterpartId), before: { merchant_favor: input.merchantFavor },
    player_offer: input.kind === 'haggle' ? [] : lines(input.playerOffer), merchant_offer: input.kind === 'trade' ? lines(input.merchantOffer) : [],
    ...(input.kind === 'training' ? { skill_var: String(input.skillVar ?? '') } : {}),
  };
  const pending = unknownOutcome.get(contentKey(body));
  if (pending && !input.operationId) return pending;
  const operation_id = input.operationId ?? newOperationId();
  const command: ExchangeCommand = { schema: EXCHANGE_SCHEMA, operation_id, ...body, plan: input.plan };
  const refusal = exchangeValueRefusal(command);
  if (refusal) throw new Refusal(refusal);
  return command;
}
/** True when the server may or may not have applied the exchange (the caller says so instead of "failed"). */
export const exchangeOutcomeUnknown = (answer: ExchangeAnswer): boolean => answer.status === 0 || answer.status >= 500;

/** POST one command; retries the SAME operation on no answer / 5xx (at most 3 attempts). */
export async function postExchange(command: ExchangeCommand, fetchImpl: typeof fetch = fetch): Promise<ExchangeAnswer> {
  const refusal = exchangeValueRefusal(command);
  if (refusal) return { ok: false, status: 400, error: refusal, body: null };
  let last: ExchangeAnswer = { ok: false, status: 0, error: 'not sent', body: null };
  for (let attempt = 0; attempt < 3; attempt++) {
    let response: Response;
    try {
      response = await fetchImpl(EXCHANGE_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(command) });
    } catch (error) {
      last = { ok: false, status: 0, error: error instanceof Error ? error.message : String(error), body: null };
      continue;
    }
    const body = await response.json().catch(() => null) as Record<string, unknown> | null;
    last = { ok: response.ok && body?.ok === true, status: response.status, error: body?.ok === true ? undefined : String(body?.error ?? response.statusText), body };
    if (last.ok || (response.status >= 400 && response.status < 500)) break;
  }
  const key = contentKey(command);
  if (exchangeOutcomeUnknown(last)) unknownOutcome.set(key, command); else unknownOutcome.delete(key);
  if (unknownOutcome.size > 64) unknownOutcome.delete(unknownOutcome.keys().next().value!);
  return last;
}

/** Roles at or above `employee` (UserRole::isAtLeast(Employee)) — the STAFF tier. */
export const EXCHANGE_STAFF_ROLES: readonly string[] = ['employee', 'admin', 'super_admin'];

/**
 * The exchange envelope's authority rule, twinned for the page (permissions are validated in the front end too): the viewer may act as
 * the acting player row when they own it, GM its campaign, or are STAFF (D-027, owner ruling 2026-09-30 — legacy's is_at_least_employee()
 * limb, restored). false only when the page can PROVE none holds (it holds the row's owner); null = unknown (the server judges).
 */
export function exchangeActorPermitted(input: { userId: string; primaryRole: string; isCampaignGm: boolean; actorOwnerId: string | null }): boolean | null {
  if (EXCHANGE_STAFF_ROLES.includes(String(input.primaryRole ?? '').trim().toLowerCase())) return true;
  if (input.isCampaignGm) return true;
  const user = upper(input.userId), owner = upper(input.actorOwnerId);
  if (!user || !owner) return null;
  return user === owner;
}

/** Recover the exact unanswered plan before another draw or UUID mint. */
export function pendingExchange(input: { kind: ExchangeKind; encounterId: string; playerId: string; counterpartId: string;
  merchantFavor: number | null; playerOffer?: ExchangeLine[]; merchantOffer?: ExchangeLine[]; skillVar?: string }): ExchangeCommand | undefined {
  const lines = (rows: ExchangeLine[] = []): ExchangeLine[] => rows.map(r => ({ inventory_id: upper(r.inventory_id), qty: Math.trunc(Number(r.qty)) }));
  const body = { kind: input.kind, encounter_id: upper(input.encounterId), actor_id: upper(input.playerId), counterpart_id: upper(input.counterpartId),
    before: { merchant_favor: input.merchantFavor }, player_offer: input.kind === 'haggle' ? [] : lines(input.playerOffer),
    merchant_offer: input.kind === 'trade' ? lines(input.merchantOffer) : [], ...(input.kind === 'training' ? { skill_var: input.skillVar ?? '' } : {}) };
  const exact = unknownOutcome.get(contentKey(body));
  if (exact) return exact;
  // A committed frame may have advanced favor while its HTTP answer was lost. Recover the old body before rolling again.
  return [...unknownOutcome.values()].find(prior => contentKey({ ...body, before: prior.before }) === contentKey(prior));
}
